import React from "react";
import { useField } from "formik";
import { object, string, boolean, type ObjectSchema } from "yup";
import { TwoFAInput, Label, Checkbox } from "@jsq/ds/core";
import {
  FormEmailInput,
  FormPasswordInput,
  Form,
  SubmitButton,
  emailInputValidationSchema,
} from "@jsq/ds/forms";
import { useLocalStorage } from "~/utils/localStorage";
import * as loginFormStyles from "./styles_vanilla.css";

type LoginFormValues = {
  email: string;
  password: string;
  verificationCode: string;
  rememberMe: boolean;
};

const validationSchema: ObjectSchema<LoginFormValues> = object({
  email: emailInputValidationSchema({ isRequired: true }),
  password: string().required("Please enter your password"),
  verificationCode: string().required("Please enter your verification code"),
  rememberMe: boolean(),
}) as ObjectSchema<LoginFormValues>;

type LoginFormProps = {
  onSubmit: (values: LoginFormValues) => Promise<void>;
};

const TwoFAInputField: React.FC = () => {
  const [, meta, helpers] = useField<string>("verificationCode");

  return (
    <TwoFAInput
      aria-labelledby="twoFaInput-label"
      onSetValue={(code) => {
        helpers.setValue(code);
      }}
      errorMessage={meta.touched && meta.error ? meta.error : undefined}
      data-cy="login-2fa"
      data-testid="login-2fa"
    />
  );
};

const ADMIN_REMEMBER_ME_KEY = "admin_remember_me";

const RememberMeField: React.FC = () => {
  const [field, , helpers] = useField<boolean>("rememberMe");

  return (
    <div className={loginFormStyles.rememberMe}>
      <Checkbox
        name="rememberMe"
        label="Remember me on this computer"
        checked={field.value}
        onChange={(e) => helpers.setValue(e.target.checked)}
      />
    </div>
  );
};

export const LoginForm: React.FC<LoginFormProps> = ({ onSubmit }) => {
  const { value: rememberedEmail } = useLocalStorage<string>(ADMIN_REMEMBER_ME_KEY);
  const initialEmail = rememberedEmail ?? "";
  const initialRememberMe = Boolean(rememberedEmail);

  return (
    <div className={loginFormStyles.loginForm}>
      <Form<LoginFormValues>
        submitOnlyChangedValues={false}
        initialValues={{
          email: initialEmail,
          password: "",
          verificationCode: "",
          rememberMe: initialRememberMe,
        }}
        yupValidationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        <FormEmailInput
          name="email"
          className={loginFormStyles.loginInput}
          data-cy="signin-email-input"
          data-testid="signin-email-input"
          isFullWidth
        />
        <FormPasswordInput
          name="password"
          placeholder=""
          className={loginFormStyles.loginInput}
          data-cy="signin-password-input"
          data-testid="signin-password-input"
          isFullWidth
        />
        <div className={loginFormStyles.twoFaInputContainer}>
          <Label id="twoFaInput-label">Two-step authentication code</Label>
          <TwoFAInputField />
        </div>
        <RememberMeField />
        <SubmitButton
          isFullWidth
          className={loginFormStyles.loginSubmitButton}
          data-cy="signin-button"
          data-testid="signin-button"
          variant="primary"
        >
          Sign in
        </SubmitButton>
      </Form>
    </div>
  );
};
